import { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { FiShield } from 'react-icons/fi';
import { FaPlus } from 'react-icons/fa6';
import { IoIosRefresh } from 'react-icons/io';
import { toast } from 'react-toastify';
import NewTooltip from '../../atoms/NewTooltip';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import PageFooter from '../../molecules/PageFooter';
import { collateralApi, ApiError } from '../../../services/collateralApi';
import LoadReceivablesModal from './ReceivableModalForm';
import CreateAnalysisPageForm from './CreateAnalysisPageForm';
import {
    formatCurrency,
    formatPercentage,
    getValidationStatusColor,
    type ValidationStatus,
} from './collateralUtils';

const STATUSES: { value: string; label: string }[] = [
    { value: 'CONCLUIDO', label: 'Concluído' },
    { value: 'PARCIALMENTE_VALIDADA', label: 'Parcialmente validada' },
    { value: 'NAO_INICIADO', label: 'Não iniciado' },
];

const ValidationPageList = () => {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [showNewForm, setShowNewForm] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });
    const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
    const [loadReceivablesRow, setLoadReceivablesRow] = useState<any>(null);
    const pageSize = 100;

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await collateralApi.getValidations({
                page, pageSize,
                status: statusFilter || undefined,
                search: search || undefined,
            });
            setRows(result.items || []);
            setTotal(result.total || 0);
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
            setStatusCounts(result.statusCounts || {});
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/access-denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, statusFilter, search]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const handleRowClick = (row: any) => {
        if (!row.totalReceivables) return;
        navigate(`/collateral/validation/${row.id}/receivables`);
    };

    const columns = [
        { id: 'op', title: 'Operação', field: 'operationCode', canSort: true, width: '120px' },
        { id: 'assignor', title: 'Cedente', field: 'assignor', canSort: true },
        { id: 'titulos', title: 'Títulos', field: 'totalReceivables', canSort: true, cssClass: 'text-center', width: '70px' },
        { id: 'sacados', title: 'Sacados', field: 'totalPayer', canSort: true, cssClass: 'text-center', width: '70px' },
        { id: 'amount', title: 'Valor', field: 'totalAmount', canSort: true, cssClass: 'text-right', width: '130px' },
        { id: 'payed', title: 'Valor Pago', field: 'validatedAmount', canSort: true, cssClass: 'text-right', width: '120px' },
        { id: 'balance', title: 'Saldo', field: 'balance', canSort: true, cssClass: 'text-right', width: '120px' },
        { id: 'percPayed', title: '% Pago', canSort: true, cssClass: 'text-right', width: '80px' },
        { id: 'percVal', title: '% Valid.', field: 'validatedPercentage', canSort: true, cssClass: 'text-right', width: '80px' },
        { id: 'status', title: 'Status', field: 'validationStatus', canSort: true, cssClass: 'text-center', width: '160px' },
    ];

    const filterGroups = initialLoading ? [] : [
        {
            title: '',
            children: (
                <div className="space-y-4">
                    <button onClick={() => setStatusFilter('')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!statusFilter
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{Object.values(statusCounts).reduce((a, b) => a + b, 0)}</span>
                        </div>
                    </button>
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">STATUS</div>
                        <div className="space-y-1">
                    {STATUSES.map(s => (
                        <button key={s.value} onClick={() => setStatusFilter(s.value)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${statusFilter === s.value
                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                            <div className="flex items-center justify-between">
                                <span>{s.label}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">{statusCounts[s.label] || 0}</span>
                            </div>
                        </button>
                    ))}
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    return (
        <>
            {isLoading && <Loading />}

            {!showNewForm && (
                <button onClick={() => setShowNewForm(true)}
                    className="fixed top-[69px] right-[10px] w-10 h-10 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-50 flex items-center justify-center border-2 border-white dark:border-slate-800 bg-blue-600 hover:bg-blue-700 text-white"
                    title="Nova Análise de Validação">
                    <FaPlus size={14} />
                </button>
            )}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiShield className="text-blue-600" size={18} />
                            <span>Garantias</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {apiInfo.version && <div>v{apiInfo.version}</div>}
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || '').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={showNewForm}
                onCloseRightPanel={() => setShowNewForm(false)}
                rightPanel={showNewForm ? (
                    <div className="h-full p-6">
                        <CreateAnalysisPageForm
                            onClose={() => setShowNewForm(false)}
                            onCreated={() => { setShowNewForm(false); setRefreshKey(k => k + 1); toast.success('Análise criada'); }}
                        />
                    </div>
                ) : null}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    {/* Header */}
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Validação de Sacados</h1>
                            <div className="flex items-center space-x-2">
                                <button onClick={() => setRefreshKey(k => k + 1)}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista">
                                    <IoIosRefresh size={18} />
                                </button>
                                <div className="w-80">
                                    <TextEdit label="Buscar cessão ou cedente..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => e.key === 'Enter' && setSearch(searchInput)} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={columns}>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={11} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhuma validação encontrada</td></tr>
                                ) : rows.map((row: any, index: number) => (
                                    <tr key={row.id}
                                        className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}
                                        onClick={() => handleRowClick(row)}>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-medium dark:text-white">
                                            <div className="flex items-center gap-2 truncate">
                                                <span className="truncate" title={row.operationCode}>{row.operationCode}</span>
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white truncate" title={row.assignor}>{row.assignor}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-sm dark:text-white">{row.totalReceivables || '—'}
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-sm dark:text-white">{row.totalPayer || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatCurrency(row.totalAmount)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatCurrency(row.validatedAmount)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatCurrency(row.balance)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatPercentage(0)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">{formatPercentage(row.validatedPercentage)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            {!row.totalReceivables && (
                                                <NewTooltip content="Carregar recebíveis" alignment="top">
                                                    <button onClick={e => { e.stopPropagation(); setLoadReceivablesRow(row); }}
                                                        className="text-white py-1 px-3 rounded-lg text-xs bg-teal-500 hover:bg-teal-700">
                                                        Recebíveis
                                                    </button>
                                                </NewTooltip>
                                            )}
                                            {row.totalReceivables > 0 && (
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getValidationStatusColor(row.validationStatus as ValidationStatus)}`}>
                                                    {row.validationStatus}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </ATable>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={page - 1} totalPages={Math.ceil(total / pageSize)} itemsPerPage={pageSize} totalElements={total} onPageChange={(p) => setPage(p + 1)} />
                    </div>
                </div>
            </ThreeColumnLayout>

            {loadReceivablesRow && (
                <LoadReceivablesModal
                    analysisId={loadReceivablesRow.id}
                    operationCode={loadReceivablesRow.operationCode}
                    offerOperationId={loadReceivablesRow.offerOperationId}
                    onClose={() => setLoadReceivablesRow(null)}
                    onSuccess={() => { setLoadReceivablesRow(null); setRefreshKey(k => k + 1); toast.success('Recebíveis carregados'); }}
                />
            )}

            <Outlet context={{ onDataChange: () => setRefreshKey(k => k + 1) }} />
        </>
    );
};

export default ValidationPageList;
